/**
 * Hand-written mock artifact for fixture mode (VITE_FIXTURE=1) — see docs/specs/app.md, "Eval /
 * acceptance". Small on purpose: 2 vendors, 5 axes, every Verdict, one subfeature citation, some
 * `compared: false` features per vendor, and proposed/dropped entries per the "Artifact totality
 * invariant" section. The real evals/fixtures/compare/artifact.sample.json (four vendors, from
 * the actual pipeline) is what's checked against for real-data quirks; this fixture is the
 * always-available, hand-controlled one for exercising every rendering path deliberately —
 * including `cost_usd: null` on one competitor and a numeric one on the other, and one feature
 * whose only evidence is third-party (which is why its cell reads `partial`, not `present`).
 *
 * The fifth axis ("LLM-ready Markdown output") is absent at both vendors on purpose: it is the
 * fixture's one gap row, which the demand block below marks `wanted` — the only combination that
 * earns the hot colour (docs/design-system.md, "Demand annotations").
 */
import type { Artifact, ArtifactDemand, DemandJoin, DemandQuote } from '../types.ts';

/**
 * Every demand quote here is invented, and the permalinks point at reserved `.example` hosts so
 * no made-up sentence is ever attributed to a real thread. Shapes follow real runs' panels.
 */
function quote(
  source: DemandQuote['source'],
  id: string,
  published_at: string,
  stance: DemandQuote['stance'],
  author_role: DemandQuote['author_role'],
  product: string | null,
  text: string,
): DemandQuote {
  const thread = id.split('-')[0];
  return {
    quote: text,
    permalink:
      source === 'hn'
        ? `https://hn.example/item?id=${id}`
        : `https://reddit.example/r/webscraping/comments/${thread}/_/${id}/`,
    utterance_id: id,
    published_at,
    stance,
    author_role,
    source,
    product,
  };
}

/** A quote with every home it has — only the ones with more than one, or a product home, carry
 * the list here; a single-join quote leaves `joins` off, the way a panel written before joins
 * existed does, so both renderings are exercised. */
const joined = (q: DemandQuote, joins: DemandJoin[]): DemandQuote => ({ ...q, joins });

const JS_RENDERING_FRICTION: DemandJoin = { kind: 'feature', company: 'ScrapingBee', product: 'ScrapingBee API', entry: 'JS Rendering', parent: null };
const WEB_SCRAPER_PRODUCT: DemandJoin = { kind: 'product', company: 'Bright Data', product: 'Web Scraper API' };

/** The one signal with two homes: a ScrapingBee render_js complaint that is friction on the JS
 * Rendering feature AND demand on the JavaScript rendering axis. It appears in both lists, once
 * each, with the same `joins`, exactly as the panel builder writes it. */
const RENDER_TIMEOUTS = joined(
  quote('reddit', 'fx601-a1', '2026-07-04T09:20:00.000Z', 'pain', 'op', 'ScrapingBee',
    'With render_js on, about one request in ten times out at thirty seconds, and I am still charged the credits for it.'),
  [JS_RENDERING_FRICTION, { kind: 'axis', axis: 'JavaScript rendering' }],
);

const NO_STANCES = { pain: 0, wish: 0, workaround: 0, switching: 0, praise: 0, rejection: 0 };

/**
 * One axis per demand verdict, plus the combination that matters: `wanted` on the gap row
 * (Markdown output, hot) against `wanted` on a row every vendor ships (JavaScript rendering,
 * neutral ink). Friction covers a feature anchor and a subfeature anchor; product-level stances
 * cover one product. The Markdown axis carries seven quotes so the display cap has something to
 * hide, plus two general voices the panel counted and did not quote (design-system.md, "General
 * voices on an axis"), and one signal (RENDER_TIMEOUTS) has two homes.
 */
const mockDemand: ArtifactDemand = {
  generated_at: '2026-08-20T15:10:00Z',
  prompt_version: 'demand-assign-1',
  axes: [
    {
      axis: 'JavaScript rendering',
      verdict: 'wanted',
      voices: 4,
      stances: { ...NO_STANCES, pain: 3, workaround: 1, praise: 1 },
      date_range: { earliest: '2025-10-02T09:14:00.000Z', latest: '2026-07-04T09:20:00.000Z' },
      quotes: [
        RENDER_TIMEOUTS,
        quote('reddit', 'fx101-a1', '2026-06-27T18:40:00.000Z', 'pain', 'op', null,
          'Half the sites I need are React shells. Without a real browser render I get an empty div and a loading spinner.'),
        quote('reddit', 'fx101-a2', '2026-06-28T07:05:00.000Z', 'praise', 'commenter', 'ScrapingBee',
          'ScrapingBee renders JS by default, which is the only reason my price tracker still works.'),
        quote('hn', 'fx102', '2026-02-11T21:30:00.000Z', 'workaround', 'commenter', null,
          'We keep a small Playwright pool around just for the pages that need JavaScript. It is the most fragile thing we run.'),
        quote('reddit', 'fx103-a1', '2025-10-02T09:14:00.000Z', 'pain', 'commenter', 'Bright Data',
          'Rendering costs five times the credits of a plain request, so I end up guessing which pages really need it.'),
      ],
    },
    {
      axis: 'AI structured extraction',
      verdict: 'disputed',
      voices: 4,
      stances: { ...NO_STANCES, wish: 2, pain: 1, rejection: 1 },
      date_range: { earliest: '2025-12-05T13:22:00.000Z', latest: '2026-08-01T10:02:00.000Z' },
      quotes: [
        quote('reddit', 'fx201-a1', '2026-08-01T10:02:00.000Z', 'wish', 'op', null,
          'I want to hand it a JSON schema and a URL and get rows back. Writing selectors for forty shop layouts is not a job.'),
        quote('hn', 'fx202', '2026-05-16T16:48:00.000Z', 'rejection', 'commenter', null,
          'I do not want a model guessing at prices. Give me deterministic selectors I can test; an LLM extractor is a silent-error machine.'),
        quote('reddit', 'fx203-a1', '2026-03-09T11:11:00.000Z', 'pain', 'commenter', 'Bright Data',
          'The pre-built parsers are fine for the big retailers and useless for everything else I scrape.'),
        quote('reddit', 'fx204-a1', '2025-12-05T13:22:00.000Z', 'wish', 'commenter', null,
          'Prompt-based extraction would let our analysts add a new source without filing a ticket with engineering.'),
      ],
    },
    {
      axis: 'Residential proxy network',
      verdict: 'rejected',
      voices: 2,
      stances: { ...NO_STANCES, rejection: 2 },
      date_range: { earliest: '2026-01-19T08:30:00.000Z', latest: '2026-04-22T19:55:00.000Z' },
      quotes: [
        quote('hn', 'fx301', '2026-04-22T19:55:00.000Z', 'rejection', 'commenter', null,
          'Residential pools are other people\'s home connections. Our legal team will not sign off on any vendor that routes through them.'),
        quote('reddit', 'fx302-a1', '2026-01-19T08:30:00.000Z', 'rejection', 'op', null,
          'I only scrape public catalog pages. Datacenter IPs are fine, and I would rather not pay the residential premium at all.'),
      ],
    },
    {
      // searched, nothing kept — the viewer must draw this with its coverage line, never as "unwanted"
      axis: 'Webhook notifications',
      verdict: 'no_signal',
      voices: 0,
      stances: { ...NO_STANCES },
      date_range: { earliest: null, latest: null },
      quotes: [],
    },
    {
      axis: 'LLM-ready Markdown output',
      verdict: 'wanted',
      voices: 9,
      stances: { ...NO_STANCES, pain: 3, wish: 2, workaround: 1, switching: 1, general: 2 },
      general_voices: 2,
      date_range: { earliest: '2025-09-14T12:00:00.000Z', latest: '2026-07-30T17:26:00.000Z' },
      quotes: [
        quote('reddit', 'fx501-a1', '2026-07-30T17:26:00.000Z', 'wish', 'op', null,
          'I just want the page back as clean markdown. Every scraping API hands me raw HTML and I burn tokens stripping nav bars before the model sees anything.'),
        quote('reddit', 'fx502-a1', '2026-06-11T06:45:00.000Z', 'pain', 'commenter', 'ScrapingBee',
          'I pipe the ScrapingBee response through my own html-to-markdown step. It works until a site changes layout, and then every table comes out as soup.'),
        quote('hn', 'fx503', '2026-05-02T22:10:00.000Z', 'workaround', 'commenter', 'Bright Data',
          'We run a readability pass and a markdown converter after Bright Data returns the HTML. Two extra services for what should be a response-format flag.'),
        quote('reddit', 'fx504-a1', '2026-03-19T14:33:00.000Z', 'wish', 'commenter', null,
          'A format=markdown parameter would let me delete a whole microservice.'),
        // a vendor's own employee: counted as a voice by the panel, marked in the viewer
        quote('hn', 'fx505', '2026-01-08T15:00:00.000Z', 'pain', 'vendor_rep', 'PageDistill',
          'We started PageDistill because none of the large scraping APIs would give our retrieval pipeline markdown. Happy to answer questions.'),
        quote('reddit', 'fx506-a1', '2025-11-23T20:18:00.000Z', 'switching', 'op', 'ScrapingBee',
          'Moved off ScrapingBee to a much smaller tool purely because it returns markdown. The proxies are worse, but my prompt bill halved.'),
        quote('reddit', 'fx507-a1', '2025-09-14T12:00:00.000Z', 'pain', 'commenter', null,
          'HTML in, garbage tokens out. Why is markdown output still not a standard option?'),
      ],
    },
  ],
  vendor_friction: [
    {
      company: 'ScrapingBee',
      product: 'ScrapingBee API',
      entry: 'JS Rendering',
      parent: null,
      quotes: [
        RENDER_TIMEOUTS,
        quote('reddit', 'fx601-a2', '2026-07-04T11:02:00.000Z', 'workaround', 'commenter', 'ScrapingBee',
          'Set an explicit wait-for selector instead of the default wait. It cut my timeouts a lot, though not to zero.'),
      ],
    },
    {
      company: 'Bright Data',
      product: 'Residential Proxies',
      entry: 'Residential rotation',
      parent: 'Residential Proxies',
      quotes: [
        quote('reddit', 'fx602-a1', '2026-05-29T16:41:00.000Z', 'pain', 'commenter', 'Bright Data',
          'Sticky sessions drop well before the timer says they should, so a multi-page checkout flow breaks halfway through.'),
      ],
    },
  ],
  // product-level: about the product as a whole, no capability in view
  product_stances: [
    {
      company: 'Bright Data',
      product: 'Web Scraper API',
      voices: 2,
      stances: { ...NO_STANCES, pain: 1, switching: 1 },
      quotes: [
        joined(
          quote('hn', 'fx603', '2026-06-18T13:07:00.000Z', 'pain', 'commenter', 'Bright Data',
            'The account verification took nine days and three video calls before I could send a single request.'),
          [WEB_SCRAPER_PRODUCT],
        ),
        joined(
          quote('reddit', 'fx604-a1', '2026-02-26T10:50:00.000Z', 'switching', 'op', 'Bright Data',
            'The dashboard has so many products and zones that I could not tell what I was being billed for. I left for something simpler.'),
          [WEB_SCRAPER_PRODUCT],
        ),
      ],
    },
  ],
  unassigned: [
    {
      capability_area: 'session and cookie persistence across requests',
      voices: 3,
      quotes: [
        quote('reddit', 'fx701-a1', '2026-07-12T08:15:00.000Z', 'pain', 'op', null,
          'Logging in works, but the next request arrives with a fresh cookie jar, so I am logged out again.'),
        quote('reddit', 'fx701-a2', '2026-07-12T09:48:00.000Z', 'workaround', 'commenter', null,
          'I export the cookies myself and send them back as a header on every call. It is ugly, but it holds.'),
        quote('hn', 'fx702', '2026-04-03T18:22:00.000Z', 'wish', 'commenter', null,
          'A named session I could reuse for an hour would remove most of my scraping glue code.'),
      ],
    },
    {
      capability_area: 'per-request cost visibility',
      voices: 1,
      quotes: [
        quote('reddit', 'fx703-a1', '2026-05-08T21:36:00.000Z', 'pain', 'op', null,
          'I find out what a request cost at the end of the month. I want the credit cost in the response headers.'),
      ],
    },
  ],
  dropped: [
    {
      ref: { permalink: 'https://reddit.example/r/webscraping/comments/fx801/_/fx801-a1/', utterance_id: 'fx801-a1' },
      reason: 'a complaint about price, not about a capability',
    },
  ],
  unread: [
    {
      ref: { permalink: 'https://reddit.example/r/webscraping/comments/fx901/_/fx901-a1/', utterance_id: 'fx901-a1' },
      cause: 'retries_exhausted',
      reason: 'batch ended after 3 re-prompts with the signal untouched',
    },
    {
      ref: { permalink: 'https://reddit.example/r/webscraping/comments/fx901/_/fx901-a2/', utterance_id: 'fx901-a2' },
      cause: 'retries_exhausted',
      reason: 'batch ended after 3 re-prompts with the signal untouched',
    },
  ],
  // the panel builder's own count over the assignments: every distinct signal quoted above (the
  // two-home one once) plus the drop, and the two unread; the retry pass was given three and
  // read one
  assignment: { signals: 29, read: 27, unassigned: 4, dropped: 1, unread: 2, retried: 3, recovered: 1 },
  coverage: [
    {
      lane: 'vendor:Bright Data',
      searches: [
        { tool: 'search_reddit', query: 'Bright Data', scope: 'webscraping', after: '2025-01-01', results: 38 },
        { tool: 'search_web', query: 'Bright Data problems frustrating alternative', scope: null, after: null, results: 10 },
      ],
      threads_fetched: 6,
      threads_kept: 4,
      date_range_seen: { earliest: '2025-10-02T09:14:00.000Z', latest: '2026-06-18T13:07:00.000Z' },
    },
    {
      lane: 'vendor:ScrapingBee',
      searches: [{ tool: 'search_reddit', query: 'ScrapingBee', scope: 'webscraping', after: '2025-01-01', results: 21 }],
      threads_fetched: 4,
      threads_kept: 3,
      date_range_seen: { earliest: '2025-11-23T20:18:00.000Z', latest: '2026-07-04T11:02:00.000Z' },
    },
    {
      lane: 'capability:JavaScript rendering',
      searches: [{ tool: 'search_reddit', query: 'javascript rendering headless scraping api', scope: 'webscraping', after: '2025-01-01', results: 17 }],
      threads_fetched: 5,
      threads_kept: 3,
      date_range_seen: { earliest: '2025-10-02T09:14:00.000Z', latest: '2026-06-28T07:05:00.000Z' },
    },
    {
      lane: 'capability:AI structured extraction',
      searches: [
        { tool: 'search_hn', query: 'llm structured extraction scraping schema', scope: null, after: null, results: 12 },
        { tool: 'search_reddit', query: 'ai extraction json schema scraper', scope: 'webscraping', after: '2025-01-01', results: 9 },
      ],
      threads_fetched: 6,
      threads_kept: 4,
      date_range_seen: { earliest: '2025-12-05T13:22:00.000Z', latest: '2026-08-01T10:02:00.000Z' },
    },
    {
      lane: 'capability:Residential proxy network',
      searches: [{ tool: 'search_hn', query: 'residential proxies scraping', scope: null, after: null, results: 14 }],
      threads_fetched: 3,
      threads_kept: 2,
      date_range_seen: { earliest: '2026-01-19T08:30:00.000Z', latest: '2026-04-22T19:55:00.000Z' },
    },
    {
      // the denominator behind the fixture's `no_signal`: searched, fetched, kept nothing
      lane: 'capability:Webhook notifications',
      searches: [
        { tool: 'search_reddit', query: 'scraping api webhook callback async job', scope: 'webscraping', after: '2025-01-01', results: 4 },
        { tool: 'search_web', query: '"webhook" scraping api job finished notification wish', scope: null, after: null, results: 9 },
      ],
      threads_fetched: 3,
      threads_kept: 0,
      date_range_seen: { earliest: null, latest: null },
    },
    {
      lane: 'capability:LLM-ready Markdown output',
      searches: [
        { tool: 'search_reddit', query: 'scraping api markdown output llm', scope: 'webscraping', after: '2025-01-01', results: 26 },
        { tool: 'search_hn', query: 'html to markdown scraping llm tokens', scope: null, after: null, results: 19 },
        { tool: 'search_web', query: 'scraping api return markdown instead of html', scope: null, after: null, results: 10 },
      ],
      threads_fetched: 9,
      threads_kept: 7,
      date_range_seen: { earliest: '2025-09-14T12:00:00.000Z', latest: '2026-07-30T17:26:00.000Z' },
    },
    {
      lane: 'idea:Fetchly',
      searches: [{ tool: 'search_web', query: '"does a tool exist" scraping api clean pages for llm pipeline', scope: null, after: null, results: 10 }],
      threads_fetched: 2,
      threads_kept: 2,
      date_range_seen: { earliest: '2026-04-03T18:22:00.000Z', latest: '2026-07-12T09:48:00.000Z' },
    },
  ],
};

export const mockArtifact: Artifact = {
  product: {
    name: 'Fetchly',
    pitch: 'A scraping API that hands LLM pipelines clean, structured pages instead of raw HTML.',
    capabilities: [
      {
        name: 'JavaScript rendering',
        description: 'load pages in a headless browser so client-side content is present before extraction',
      },
      {
        name: 'AI structured extraction',
        description: 'turn a page into structured data from a schema or natural-language prompt',
      },
      {
        name: 'Residential proxy network',
        description: 'egress through residential IPs, not just datacenter ranges',
      },
      {
        name: 'Webhook notifications',
        description: 'push a notification to my endpoint when an async job finishes',
      },
      {
        name: 'LLM-ready Markdown output',
        description: 'return the page as clean Markdown, stripped of navigation and boilerplate',
      },
    ],
  },
  generated_at: '2026-08-20T14:30:00Z',
  run: {
    id: 'run-fixture-1',
    competitors: [
      {
        slug: 'brightdata',
        company: 'Bright Data',
        landing: 'https://brightdata.com',
        status: 'done',
        pages_crawled: 42,
        // Untracked, like the real sample artifact's stage-by-stage corpus builds — exercises
        // the "—" rendering path (formatCost) rather than a fabricated $0.00.
        cost_usd: null,
        error: null,
      },
      {
        slug: 'scrapingbee',
        company: 'ScrapingBee',
        landing: 'https://www.scrapingbee.com',
        status: 'done',
        pages_crawled: 18,
        cost_usd: 0.44,
        error: null,
      },
    ],
  },
  matrix: {
    axes: [
      {
        capability: 'JavaScript rendering',
        description: 'load pages in a headless browser so client-side content is present before extraction',
        source: 'user',
      },
      {
        capability: 'AI structured extraction',
        description: 'turn a page into structured data from a schema or natural-language prompt',
        source: 'user',
      },
      {
        capability: 'Residential proxy network',
        description: 'egress through residential IPs, not just datacenter ranges',
        source: 'user',
      },
      {
        capability: 'Webhook notifications',
        description: 'push a notification to my endpoint when an async job finishes',
        source: 'user',
      },
      {
        capability: 'LLM-ready Markdown output',
        description: 'return the page as clean Markdown, stripped of navigation and boilerplate',
        source: 'user',
      },
    ],
    cells: [
      // Bright Data: present, partial, present_as_subfeature, unknown
      {
        axis: 'JavaScript rendering',
        company: 'Bright Data',
        verdict: 'present',
        entry: 'Scraping Browser',
        parent: null,
        product: 'Scraping Browser',
        kind: 'feature',
        evidence: [
          {
            url: 'https://brightdata.com/products/scraping-browser',
            third_party: false,
            source_kind: null,
            quote: 'a headless browser served through our proxy network, fully rendering JavaScript',
          },
        ],
        note: null,
      },
      {
        axis: 'AI structured extraction',
        company: 'Bright Data',
        verdict: 'partial',
        entry: 'Web Scraper API',
        parent: null,
        product: 'Web Scraper API',
        kind: 'feature',
        evidence: [
          {
            url: 'https://brightdata.com/products/web-scraper-api',
            third_party: false,
            source_kind: null,
            quote: 'pre-built parsers return structured fields for supported domains',
          },
        ],
        note: 'only covers a fixed set of pre-built domain parsers, not arbitrary schema/prompt-driven extraction',
      },
      {
        axis: 'Residential proxy network',
        company: 'Bright Data',
        verdict: 'present_as_subfeature',
        entry: 'Residential rotation',
        parent: 'Residential Proxies',
        product: 'Residential Proxies',
        kind: 'subfeature',
        evidence: [
          {
            url: 'https://brightdata.com/proxy-types/residential-proxies',
            third_party: false,
            source_kind: null,
            quote: 'IPs rotate automatically on every request or on a sticky session timer',
          },
        ],
        note: null,
      },
      {
        axis: 'Webhook notifications',
        company: 'Bright Data',
        verdict: 'unknown',
        entry: null,
        parent: null,
        product: null,
        kind: null,
        evidence: [],
        note: 'no mention of job-completion callbacks found in the crawled docs/pricing pages',
      },
      // ScrapingBee: present, present, absent, partial (the last capped from present because its
      // only evidence is third-party) — with Bright Data above, all five verdicts are covered
      {
        axis: 'JavaScript rendering',
        company: 'ScrapingBee',
        verdict: 'present',
        entry: 'JS Rendering',
        parent: null,
        product: 'ScrapingBee API',
        kind: 'feature',
        evidence: [
          {
            url: 'https://www.scrapingbee.com/features/',
            third_party: false,
            source_kind: null,
            quote: 'renders every page in a real Chrome browser before returning the HTML',
          },
        ],
        note: null,
      },
      {
        axis: 'AI structured extraction',
        company: 'ScrapingBee',
        verdict: 'present',
        entry: 'AI Extraction',
        parent: null,
        product: 'ScrapingBee API',
        kind: 'feature',
        evidence: [
          {
            url: 'https://www.scrapingbee.com/features/ai-extraction/',
            third_party: false,
            source_kind: null,
            quote: 'describe the fields you want in plain English and get back clean JSON',
          },
        ],
        note: null,
      },
      {
        axis: 'Residential proxy network',
        company: 'ScrapingBee',
        verdict: 'absent',
        entry: null,
        parent: null,
        product: null,
        kind: null,
        evidence: [],
        note: 'proxy pages describe only datacenter and premium (mixed-source) pools, never residential',
      },
      {
        // capped at partial by the judge's applier: the only evidence is third-party
        axis: 'Webhook notifications',
        company: 'ScrapingBee',
        verdict: 'partial',
        entry: 'Async Webhooks',
        parent: null,
        product: 'ScrapingBee API',
        kind: 'feature',
        evidence: [
          {
            url: 'https://www.reddit.com/r/webscraping/comments/1abcdef/scrapingbee_webhooks/',
            third_party: true,
            source_kind: 'forum',
            quote: 'it POSTs the result when the job finishes',
          },
        ],
        note: "capped at partial: the only evidence for this is third-party (forum), not the vendor's own documentation",
      },
      // The gap row: absent at both vendors, and (below) wanted.
      {
        axis: 'LLM-ready Markdown output',
        company: 'Bright Data',
        verdict: 'absent',
        entry: null,
        parent: null,
        product: null,
        kind: null,
        evidence: [],
        note: 'every response format documented is HTML or parsed JSON; no Markdown or text-only output',
      },
      {
        axis: 'LLM-ready Markdown output',
        company: 'ScrapingBee',
        verdict: 'absent',
        entry: null,
        parent: null,
        product: null,
        kind: null,
        evidence: [],
        note: 'the API reference lists HTML, JSON and screenshot responses only',
      },
    ],
    coverage: [
      { company: 'Bright Data', pages_read: 42, pages_known: 51 },
      { company: 'ScrapingBee', pages_read: 18, pages_known: 20 },
    ],
  },
  vendors: [
    {
      slug: 'brightdata',
      company: 'Bright Data',
      coverage: { pages_read: 42, pages_known: 51 },
      proposed: [
        { name: 'Data Collector', product: 'Data Collector' },
        { name: 'Bulk Request Discounts', product: 'Web Scraper API' },
      ],
      dropped: [
        {
          name: 'Enterprise SLA',
          product: 'Web Scraper API',
          reason: "an SLA is a contract term, not a product capability",
        },
      ],
      features: [
        {
          name: 'Scraping Browser',
          product: 'Scraping Browser',
          aliases: [],
          mentions: [
            {
              url: 'https://brightdata.com/products/scraping-browser',
              third_party: false,
              source_kind: null,
              description: 'a headless browser served through our proxy network, fully rendering JavaScript',
            },
          ],
          subfeatures: [],
          compared: true,
          cited_by: ['JavaScript rendering'],
        },
        {
          name: 'Web Scraper API',
          product: 'Web Scraper API',
          aliases: [],
          mentions: [
            {
              url: 'https://brightdata.com/products/web-scraper-api',
              third_party: false,
              source_kind: null,
              description: 'pre-built parsers return structured fields for supported domains',
            },
          ],
          subfeatures: [],
          compared: true,
          cited_by: ['AI structured extraction'],
        },
        {
          name: 'Residential Proxies',
          product: 'Residential Proxies',
          aliases: ['Resi Proxies'],
          mentions: [
            {
              url: 'https://brightdata.com/proxy-types/residential-proxies',
              third_party: false,
              source_kind: null,
              description: '72M+ residential IPs across 195 countries',
            },
          ],
          subfeatures: [
            {
              name: 'Residential rotation',
              mentions: [
                {
                  url: 'https://brightdata.com/proxy-types/residential-proxies',
                  third_party: false,
                  source_kind: null,
                  description: 'IPs rotate automatically on every request or on a sticky session timer',
                },
              ],
              compared: true,
              cited_by: ['Residential proxy network'],
            },
            {
              name: 'City-level targeting',
              mentions: [
                {
                  url: 'https://brightdata.com/proxy-types/residential-proxies',
                  third_party: false,
                  source_kind: null,
                  description: 'target by country, state, or city for the majority of covered locations',
                },
              ],
              compared: false,
              cited_by: [],
            },
          ],
          compared: true,
          cited_by: ['Residential proxy network'],
        },
        {
          name: 'Web Unlocker',
          product: 'Web Unlocker',
          aliases: [],
          mentions: [
            {
              url: 'https://brightdata.com/products/web-unlocker',
              third_party: false,
              source_kind: null,
              description: 'automatically bypasses CAPTCHAs and bot-detection challenges per request',
            },
          ],
          subfeatures: [
            {
              name: 'CAPTCHA solving',
              mentions: [
                {
                  url: 'https://brightdata.com/products/web-unlocker',
                  third_party: false,
                  source_kind: null,
                  description: 'CAPTCHAs are detected and solved without any caller-side handling',
                },
              ],
              compared: false,
              cited_by: [],
            },
          ],
          compared: false,
          cited_by: [],
        },
      ],
    },
    {
      slug: 'scrapingbee',
      company: 'ScrapingBee',
      coverage: { pages_read: 18, pages_known: 20 },
      proposed: [{ name: 'Concurrency Add-on', product: 'ScrapingBee API' }],
      dropped: [],
      features: [
        {
          name: 'JS Rendering',
          product: 'ScrapingBee API',
          aliases: ['JavaScript Rendering'],
          mentions: [
            {
              url: 'https://www.scrapingbee.com/features/',
              third_party: false,
              source_kind: null,
              description: 'renders every page in a real Chrome browser before returning the HTML',
            },
          ],
          subfeatures: [],
          compared: true,
          cited_by: ['JavaScript rendering'],
        },
        {
          name: 'AI Extraction',
          product: 'ScrapingBee API',
          aliases: [],
          mentions: [
            {
              url: 'https://www.scrapingbee.com/features/ai-extraction/',
              third_party: false,
              source_kind: null,
              description: 'describe the fields you want in plain English and get back clean JSON',
            },
          ],
          subfeatures: [],
          compared: true,
          cited_by: ['AI structured extraction'],
        },
        {
          name: 'Screenshot API',
          product: 'ScrapingBee API',
          aliases: [],
          mentions: [
            {
              url: 'https://www.scrapingbee.com/features/screenshot-api/',
              third_party: false,
              source_kind: null,
              description: 'full-page or above-the-fold screenshots as PNG or JPEG',
            },
          ],
          subfeatures: [],
          compared: false,
          cited_by: [],
        },
        // The third-party path, so fixture mode renders the chip, the mixed-evidence feature and
        // the third-party-evidence facet without needing a real discovery run.
        {
          name: 'Async Webhooks',
          product: 'ScrapingBee API',
          aliases: [],
          mentions: [
            {
              url: 'https://www.reddit.com/r/webscraping/comments/1abcdef/scrapingbee_webhooks/',
              third_party: true,
              source_kind: 'forum',
              description: 'you pass a webhook url on the async endpoint and it POSTs the result when the job finishes',
            },
          ],
          subfeatures: [],
          compared: true,
          cited_by: ['Webhook notifications'],
        },
        {
          name: 'Google Search API',
          product: 'ScrapingBee API',
          aliases: [],
          mentions: [
            {
              url: 'https://www.scrapingbee.com/features/google-search-api/',
              third_party: false,
              source_kind: null,
              description: 'query Google and get back structured, parsed search results',
            },
          ],
          subfeatures: [
            {
              name: 'Localized results',
              mentions: [
                {
                  url: 'https://www.scrapingbee.com/features/google-search-api/',
                  third_party: false,
                  source_kind: null,
                  description: 'set country and language to get localized result pages',
                },
              ],
              compared: false,
              cited_by: [],
            },
          ],
          compared: false,
          cited_by: [],
        },
      ],
    },
  ],
  demand: mockDemand,
};
